import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Save, ImagePlus, Trash2, X, Info } from "lucide-react";

export function useSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = () => {
    setLoading(true);
    api.get("/vendor/settings")
      .then(({ data }) => setSettings(data))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);
  return { settings, setSettings, loading, reload };
}

export function SectionHeader({ overline, title, subtitle }) {
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <div className="mb-4 sm:mb-6">
      <div className="overline">{overline}</div>
      <div className="flex items-center gap-2 mt-1">
        <h2 className="font-heading text-xl sm:text-3xl font-medium tracking-tight text-slate-900">{title}</h2>
        {subtitle && (
          <button type="button" onClick={() => setHelpOpen((o) => !o)} className="sm:hidden h-6 w-6 rounded-full border border-slate-300 text-slate-400 hover:border-violet-400 hover:text-violet-600 flex items-center justify-center shrink-0 transition-colors" data-testid="section-help-toggle" aria-label="Afficher l'aide">
            <Info className="h-3 w-3" />
          </button>
        )}
      </div>
      {subtitle && <p className={`text-sm text-slate-500 mt-2 max-w-2xl ${helpOpen ? "block" : "hidden"} sm:block`}>{subtitle}</p>}
    </div>
  );
}

export function SaveBar({ dirty, saving, onSave, testid }) {
  return (
    <div className="sticky bottom-0 -mx-6 mt-8 px-6 py-4 bg-white/90 backdrop-blur border-t border-slate-200 flex items-center justify-end gap-3">
      {dirty && <span className="text-xs text-amber-600 font-medium">Modifications non enregistrées</span>}
      <button
        onClick={onSave}
        disabled={!dirty || saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        data-testid={testid}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
      </button>
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, suffix, type = "text", testid, hint }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">{label}</div>
      <div className="relative">
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
          placeholder={placeholder}
          className="w-full h-10 px-3 pr-10 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
          data-testid={testid}
        />
        {suffix && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-medium pointer-events-none">{suffix}</div>}
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

export function TextArea({ label, value, onChange, rows = 4, testid, hint }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">{label}</div>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 resize-y"
        data-testid={testid}
      />
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

export function Toggle({ label, checked, onChange, hint, testid }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 cursor-pointer transition-colors" data-testid={testid}>
      <div className="mt-0.5 shrink-0">
        <div className={`h-5 w-9 rounded-full transition-colors relative ${checked ? "bg-violet-600" : "bg-slate-200"}`}>
          <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? "left-4" : "left-0.5"}`} />
        </div>
        <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}

const VAT_PRESETS = [0, 5.5, 8.5, 10, 20];

export function VatSelector({ value, onChange, testid = "vat" }) {
  const isCustom = !VAT_PRESETS.includes(Number(value));
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">Taux de TVA appliqué</div>
      <div className="flex flex-wrap items-center gap-2">
        {VAT_PRESETS.map((rate) => (
          <button
            key={rate}
            type="button"
            onClick={() => onChange(rate)}
            className={`px-3 h-9 rounded-full text-xs font-semibold transition-colors ${Number(value) === rate && !isCustom ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-violet-400"}`}
            data-testid={`${testid}-preset-${String(rate).replace('.', '_')}`}
          >
            {rate.toString().replace(".", ",")} %
          </button>
        ))}
        <div className={`relative ${isCustom ? "ring-2 ring-violet-500 rounded-full" : ""}`}>
          <input
            type="number"
            step="0.1"
            min="0"
            max="99"
            value={isCustom ? value : ""}
            placeholder="Autre…"
            onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
            className="w-24 h-9 pl-3 pr-6 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 focus:outline-none focus:border-violet-500"
            data-testid={`${testid}-custom`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">%</span>
        </div>
      </div>
    </div>
  );
}

export const DAYS = [
  ["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"],
  ["thursday", "Jeudi"], ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"],
];

export function HoursTable({ value, onChange, testid }) {
  const set = (day, field, val) => onChange({ ...value, [day]: { ...(value[day] || {}), [field]: val } });
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
      {DAYS.map(([key, label]) => {
        const h = value[key] || { open: "09:00", close: "18:00", closed: false };
        return (
          <div key={key} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 border-slate-100">
            <div className="w-24 text-xs font-medium text-slate-700">{label}</div>
            {h.closed ? (
              <div className="flex-1 text-xs text-slate-400 italic">Fermé</div>
            ) : (
              <>
                <input type="time" value={h.open} onChange={(e) => set(key, "open", e.target.value)} className="h-8 px-2 rounded-md border border-slate-200 text-xs" data-testid={`${testid}-${key}-open`} />
                <span className="text-xs text-slate-400">→</span>
                <input type="time" value={h.close} onChange={(e) => set(key, "close", e.target.value)} className="h-8 px-2 rounded-md border border-slate-200 text-xs" data-testid={`${testid}-${key}-close`} />
              </>
            )}
            <button type="button" onClick={() => set(key, "closed", !h.closed)} className={`ml-auto text-[10px] font-semibold px-2 py-1 rounded-full transition-colors ${h.closed ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>
              {h.closed ? "Fermé" : "Ouvert"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function InfoPopover({ title, points, open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-violet-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl p-6" data-testid="info-popover">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors" data-testid="info-popover-close" aria-label="Fermer">
          <X className="h-4 w-4" />
        </button>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold tracking-wider uppercase">
          <Info className="h-3 w-3" /> Comment faire
        </div>
        <h3 className="font-heading text-lg font-semibold text-slate-900 mt-3">{title}</h3>
        <ul className="mt-3 space-y-2.5">
          {points.map((pt, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-violet-400 shrink-0" />
              <span>{pt}</span>
            </li>
          ))}
        </ul>
        <button onClick={onClose} className="mt-5 w-full h-10 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-700 transition-colors" data-testid="info-popover-ok">
          Compris !
        </button>
      </div>
    </div>
  );
}

export function LogoUploader({ value, onChange, testid = "logo-upload" }) {
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Image trop lourde (max 1,5 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(f);
    e.target.value = "";
  };
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1.5">Logo de l'entreprise</div>
      {value ? (
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-xl border border-slate-200 bg-white p-1.5 flex items-center justify-center overflow-hidden">
            <img src={value} alt="Logo" className="max-h-full max-w-full object-contain" />
          </div>
          <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 cursor-pointer hover:border-violet-400 transition-colors">
            <ImagePlus className="h-3.5 w-3.5" /> Changer
            <input type="file" accept="image/*" onChange={onFile} className="sr-only" data-testid={`${testid}-input`} />
          </label>
          <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors" data-testid={`${testid}-remove`}>
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center gap-1.5 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 cursor-pointer hover:border-violet-400 hover:bg-violet-50/40 transition-colors" data-testid={testid}>
          <ImagePlus className="h-5 w-5 text-slate-400" />
          <span className="text-xs font-medium text-slate-500">Importer un logo (PNG, JPG — max 1,5 Mo)</span>
          <input type="file" accept="image/*" onChange={onFile} className="sr-only" data-testid={`${testid}-input`} />
        </label>
      )}
      <div className="text-[11px] text-slate-400 mt-1">Apposé sur vos devis, contrats et factures.</div>
    </div>
  );
}
